import { action, computed, makeObservable, observable } from 'mobx';
import { PublicStore } from '.';
import {
  listThreads, loadAliases, removeThread, saveAliases, saveThread, StoredMessage, Thread,
} from '../../agent/history';
import { AgentProgress, AgentStepLine, nextSteps } from '../components/common/AgentPanel/types';
import type { ExperienceCandidate } from '../../agent/runtime/experience';
import type { TaskSpec } from '../../agent/runtime/task';
import { userMessage } from '../../agent/runtime/userMessage';
import { dropDuplicates, uploadTrouble } from '../../agent/runtime/upload';
import type { UploadedFile } from '../../agent/runtime/upload';
import type { QueuedJob } from '../../agent/runtime/queued';

/** 서버에 맡긴 일을 얼마나 자주 들여다보나. 대기열 항목이 「완료」로 바뀌는 것만 본다. */
const WATCH_MS = 2000;
/** 이만큼 지나도 안 끝나면 한 번 「아직 처리 중」이라 알린다. */
const LATE_MS = 10 * 60_000;
/** 이만큼 지나면 그만 지켜본다. 대기열의 재시도는 사람이 한다. */
const GIVE_UP_MS = 60 * 60_000;

/**
 * 고른 파일을 base64 로 읽는다.
 *
 * `FileReader` 는 `data:<mime>;base64,<본문>` 을 준다. 게이트웨이가 다시 그 모양으로
 * 감싸므로 여기서는 본문만 떼어 낸다 — 두 번 감싸면 모델이 못 읽는다.
 */
function readAsUpload(file: File): Promise<UploadedFile> {
  return new Promise((done, fail) => {
    const reader = new FileReader();
    reader.onerror = () => fail(new Error(`${file.name} 을(를) 읽지 못했습니다.`));
    reader.onload = () => {
      const url = String(reader.result ?? '');
      done({ name: file.name, mime: file.type, data: url.slice(url.indexOf(',') + 1) });
    };
    reader.readAsDataURL(file);
  });
}

export type Message = StoredMessage & { progress?: AgentProgress; steps?: AgentStepLine[] };

type Turn = {
  question: string; answer: string; routes?: string[]; table?: StoredMessage['table'];
  feedback?: StoredMessage['feedback'];
  result?: StoredMessage['result'];
  report?: StoredMessage['report'];
  task?: TaskSpec;
  /** 그 턴이 되묻기였는가. 되묻기의 본문은 답이 아니라 우리가 낸 질문이다. */
  asked?: boolean;
};

function turnsOf(messages: Message[]): Turn[] {
  const turns: Turn[] = [];
  for (let i = 0; i < messages.length - 1; i += 1) {
    if (messages[i].role === 'user' && messages[i + 1].role === 'assistant') {
      turns.push({
        question: messages[i].text,
        answer: messages[i + 1].text,
        routes: messages[i + 1].routes,
        table: messages[i + 1].table,
        feedback: messages[i + 1].feedback,
        result: messages[i + 1].result,
        report: messages[i + 1].report,
        task: messages[i + 1].task,
        // 되묻기의 본문은 우리가 낸 질문이다. 답으로 세면 파일 재사용이 그 질문을
        // 보고서 본문으로 만든다 — 실제로 그런 워드가 나왔다.
        asked: messages[i + 1].progress?.kind === 'ask',
      });
    }
  }
  return turns;
}

function words(value: string): Set<string> {
  return new Set(value.replace(/[^0-9A-Za-z가-힣]+/g, ' ').split(/\s+/).filter(Boolean));
}

function overlap(question: string, experience: ExperienceCandidate): number {
  const wanted = words(question);
  const saved = words(experience.question);
  return Array.from(wanted).filter((word) => saved.has(word)).length / Math.max(1, wanted.size);
}

export default class AgentStore {
  @observable isOpen = false;
  @observable threads: Thread[] = [];
  @observable activeId: string | null = null;
  @observable busy = false;

  /**
   * **앞 작업이 도는 동안 받은 질문.** 버리지 않고 줄을 세운다.
   *
   * 예전에는 `busy` 면 `ask` 가 그냥 `return` 했다. 질문은 큐에 안 쌓이고, 「처리
   * 중입니다」라는 말도 없고, 답도 안 왔다 — 사람 눈에는 **누른 것이 먹통이 된 것처럼**
   * 보였다. 한 턴이 몇 분씩 걸리면 그 동안 누르는 족족 사라졌다. 사용자가 「응답이
   * 없다」, 「한 세션에서 다른 걸 주문하면 아예 멈춘다」로 보고한 것이 이 모양이다.
   *
   * 취소하면 줄도 비운다. 앞 것을 그만두라고 한 사람이 뒤의 것까지 돌기를 바라지는
   * 않는다.
   */
  @observable queued: string[] = [];

  /**
   * 자율 모드. 켜면 저장·삭제와 메일·문자·팩스·카카오톡 전송까지 대신한다.
   *
   * **로그아웃 전까지만** 산다 — 스토어에만 두고 대화나 IndexedDB 에 저장하지 않는다.
   * 다시 로그인하면 꺼진 채로 시작한다.
   */
  @observable auto = false;

  /**
   * 사람이 말한 이름 → 화면이 확정한 이름. 대화를 넘어 사용자에게 붙는다.
   *
   * 한 대화 안에서만 기억하면 새 대화마다 같은 현장을 다시 고르게 된다.
   */
  private aliases: Record<string, string> = {};
  @observable showingHistory = false;

  /**
   * 사람이 이번 턴에 붙인 파일.
   *
   * **보낸 뒤에 비운다.** 남겨 두면 다음 질문마다 같은 그림이 따라가 토큰을 태우고,
   * 사람은 왜 엉뚱한 이력서를 다시 읽는지 모른다.
   */
  @observable attached: UploadedFile[] = [];

  /** 파일을 못 받은 이유. 붙이는 자리에서 바로 보인다. */
  @observable attachTrouble = '';

  /**
   * **이번 턴에 보낸 파일.** 문서에 붙이려면 보낸 뒤에도 잠깐 들고 있어야 한다.
   *
   * `attached` 는 보내는 순간 비운다(다음 질문에 따라가지 않게). 그런데 에이전트가
   * 「이 파일 첨부해줘」를 처리하려면 그 파일이 필요하다 — 그래서 한 턴만 여기 남긴다.
   * 다음 턴이 시작되면 덮인다.
   */
  private turnFiles: UploadedFile[] = [];

  /** 에이전트가 문서에 붙일 파일. 이번 턴에 올린 것뿐이다. */
  filesOfTurn(): UploadedFile[] {
    return this.turnFiles;
  }

  private controller?: AbortController;

  /**
   * **서버에 맡긴 일을 지켜보는 타이머.** 일마다 하나다(`key@시작시각`).
   *
   * 지로일괄발행·국세청 매출처리처럼 대기열(lazytask)로 넘어간 일은 몇 분 뒤 MQTT 알림으로
   * 끝난다. 턴은 그것을 기다리지 않고 끝나고, 여기서 지켜보다 끝나면 **그 일을 시킨 대화**에
   * 알린다. 결과(파일 받기 등)는 사람이 「결과 받기」를 눌러야 나온다 — 앱도 대기열의 「실행」을
   * 사람이 누르게 한다.
   */
  private watching = new Map<string, number>();

  constructor(readonly publicStore: PublicStore) {
    makeObservable(this);
  }

  /**
   * 보고 문서의 작성 부서·작성자 — 로그인한 사람(부서명·이름). 그 밖의 계정 정보는 문서에 넣지 않는다.
   */
  get reportAuthor(): string {
    const { user } = this.publicStore;
    return [user?.divinm, user?.pernm].map((one) => String(one ?? '').trim()).filter(Boolean).join(' ');
  }

  private get owner(): string | null {
    const { user } = this.publicStore;
    return user?.custcd && user?.perid ? `${user.custcd}:${user.perid}` : null;
  }

  @computed
  get messages(): Message[] {
    return (this.threads.find((thread) => thread.id === this.activeId)?.messages ?? []) as Message[];
  }

  @action
  setAuto(value: boolean): void {
    this.auto = value;
  }

  /**
   * 파일을 붙인다. **보내기 전에** 읽을 수 있는지 본다.
   *
   * 게이트웨이도 같은 검사를 하지만 거기까지 가면 올린 시간이 통째로 버려진다.
   * 잣대는 런타임의 것을 그대로 쓴다 — 두 벌이 되면 한쪽만 고쳐진다.
   */
  @action
  async attach(chosen: File[]): Promise<void> {
    this.attachTrouble = '';
    let read: UploadedFile[];
    try {
      read = await Promise.all(chosen.map(readAsUpload));
    } catch (error) {
      this.attachTrouble = error instanceof Error ? error.message : '파일을 읽지 못했습니다.';
      return;
    }
    // **같은 파일을 두 번 올리지 않는다.** 사람은 붙였는지 잊고 다시 고른다. 그대로
    // 두면 같은 그림이 두 번 실려 토큰을 두 배로 태우고, 문서에도 두 번 붙는다.
    // 가르는 규칙은 런타임에 둔다 — 여기 두면 시험이 안 걸린다.
    const merged = [...this.attached, ...dropDuplicates(this.attached, read)];
    if (merged.length === this.attached.length) {
      this.attachTrouble = '이미 붙인 파일입니다.';
      return;
    }
    const trouble = uploadTrouble(merged);
    // 문제가 있으면 **하나도 붙이지 않는다.** 일부만 붙으면 사람은 무엇이 갔는지 모른다.
    if (trouble) {
      this.attachTrouble = trouble;
      return;
    }
    this.attached = merged;
  }

  @action
  detach(index: number): void {
    this.attached = this.attached.filter((_one, at) => at !== index);
    this.attachTrouble = '';
  }

  @action
  async toggle(): Promise<void> {
    this.isOpen = !this.isOpen;
    if (this.isOpen && !this.threads.length) await this.load();
  }

  @action
  async load(): Promise<void> {
    const owner = this.owner;
    if (!owner) return;
    this.threads = await listThreads(owner);
    this.aliases = await loadAliases(owner).catch(() => ({}));
    // 회사가 공유하는 짝을 밑에 깔고, 이 브라우저가 아는 것을 그 위에 얹는다.
    // 서버가 없어도 각자 브라우저의 기억으로 돈다.
    const shared = await (window.__elmanAgentAliases?.() ?? Promise.resolve({})).catch(() => ({}));
    if (Object.keys(shared).length) this.aliases = { ...shared, ...this.aliases };
    this.activeId = this.threads[0]?.id ?? null;
    if (!this.activeId) this.newThread();
  }

  @action
  newThread(): void {
    const thread: Thread = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: '',
      updatedAt: Date.now(),
      messages: [],
    };
    this.threads = [thread, ...this.threads];
    this.activeId = thread.id;
    this.showingHistory = false;
  }

  @action
  openThread(id: string): void {
    this.activeId = id;
    this.showingHistory = false;
  }

  @action
  async deleteThread(id: string): Promise<void> {
    const owner = this.owner;
    this.threads = this.threads.filter((thread) => thread.id !== id);
    if (owner) await removeThread(owner, id);
    if (this.activeId === id) this.activeId = this.threads[0]?.id ?? null;
    if (!this.activeId) this.newThread();
  }

  cancel(): void {
    this.controller?.abort();
    // 앞 것을 그만두라고 한 사람이 뒤의 것까지 돌기를 바라지는 않는다.
    this.queued = [];
  }

  /** 줄에서 하나 뺀다. 사람이 마음을 바꿀 수 있어야 한다. */
  unqueue(at: number): void {
    this.queued = this.queued.filter((_, index) => index !== at);
  }

  private learned(question: string): ExperienceCandidate[] {
    const found = this.threads.flatMap((thread) => thread.messages)
      .filter((message) => message.role === 'assistant'
        && message.feedback === 'positive' && message.experience)
      .map((message) => message.experience as ExperienceCandidate);
    return found.sort((left, right) => overlap(question, right) - overlap(question, left)).slice(0, 3);
  }

  @action
  async rate(messageIndex: number, feedback: 'positive' | 'negative'): Promise<void> {
    const messages = this.messages.slice();
    const message = messages[messageIndex];
    if (!message || message.role !== 'assistant' || !message.experience) return;
    messages[messageIndex] = { ...message, feedback };
    this.write(messages);
    await this.persist();
    if (feedback === 'positive') {
      try {
        await window.__elmanAgentPromote?.(message.experience);
      } catch (error) {
        // 조직 RAG가 잠시 없어도 사용자의 평가와 로컬 학습은 보존한다.
        // 다만 조용히 삼키면 승격이 한 번도 안 됐다는 사실도 같이 묻힌다.
        // eslint-disable-next-line no-console
        console.debug('[agent] 경험 승격 실패', String(error).slice(0, 200));
      }
    }
  }

  /** 끝난 일의 결과를 연다 — 앱이 대기열의 「실행」에서 하는 그대로다. */
  runFinished(job: QueuedJob): boolean {
    const item = this.findQueued(job);
    if (!item?.isDone) return false;
    this.publicStore.waitQueueStore.doAction(item);
    return true;
  }

  /** 그 일의 결과를 아직 열 수 있나. 대기열에서 이미 실행했거나 지웠으면 못 연다. */
  canRunFinished(job: QueuedJob): boolean {
    return Boolean(this.findQueued(job)?.isDone);
  }

  private findQueued(job: QueuedJob) {
    return this.publicStore.waitQueueStore.data
      .find((item) => item.key === job.key && item.intime.getTime() === job.startedAt);
  }

  private watchQueued(threadId: string, jobs: QueuedJob[]): void {
    for (const job of jobs) {
      const id = `${job.key}@${job.startedAt}`;
      if (this.watching.has(id)) continue;
      let late = false;
      const stop = () => {
        window.clearInterval(this.watching.get(id));
        this.watching.delete(id);
      };
      this.watching.set(id, window.setInterval(() => {
        const item = this.findQueued(job);
        const elapsed = Date.now() - job.startedAt;
        if (item?.isDone) {
          stop();
          this.notify(threadId, `${job.title} 작업이 끝났습니다. 아래 「결과 받기」를 누르면 결과를 엽니다.`, job);
        } else if (!item) {
          // 사람이 대기열 창에서 먼저 실행했거나 지웠다. 결과는 그쪽에서 이미 열렸다.
          stop();
          this.notify(threadId, `${job.title} 작업이 대기열에서 처리됐거나 지워졌습니다.`);
        } else if (elapsed > GIVE_UP_MS) {
          stop();
          this.notify(threadId, `${job.title} 작업이 한 시간이 지나도 끝나지 않았습니다. 화면 위쪽 대기열에서 다시 시도할 수 있습니다.`);
        } else if (!late && elapsed > LATE_MS) {
          late = true;
          this.notify(threadId, `${job.title} 작업이 아직 서버에서 처리 중입니다. 끝나면 다시 알려 드리겠습니다.`);
        }
      }, WATCH_MS));
    }
  }

  /** 일을 시킨 대화에 알림을 붙인다. 지금 보고 있는 대화가 아닐 수 있다. */
  @action
  private notify(threadId: string, text: string, finished?: QueuedJob): void {
    const thread = this.threads.find((candidate) => candidate.id === threadId);
    if (!thread) return;
    thread.messages = [
      ...thread.messages,
      {
        role: 'assistant', text, progress: { kind: 'done', text }, ...(finished ? { finished } : {}),
      } as Message,
    ];
    thread.updatedAt = Date.now();
    this.threads = [...this.threads];
    void this.persist(threadId);
  }

  @action
  private write(messages: Message[]): void {
    const thread = this.threads.find((candidate) => candidate.id === this.activeId);
    if (!thread) return;
    thread.messages = messages;
    thread.updatedAt = Date.now();
    if (!thread.title) {
      thread.title = messages.find((message) => message.role === 'user')?.text.slice(0, 30) ?? '';
    }
    this.threads = [...this.threads];
  }

  private async persist(threadId = this.activeId): Promise<void> {
    const owner = this.owner;
    const thread = this.threads.find((candidate) => candidate.id === threadId);
    if (!owner || !thread || !thread.title) return;
    // IndexedDB 는 구조화 복제로 값을 받는데 mobx observable 은 프록시라 복제되지 않는다
    // (`#<Object> could not be cloned`). 평범한 객체로 만들어 넘긴다. `progress` 는
    // 진행 중에만 쓰는 것이라 같이 떨어져 나가도 된다.
    await saveThread(owner, JSON.parse(JSON.stringify(thread)) as Thread);
  }

  async ask(question: string): Promise<void> {
    const run = window.__elmanAgentAsk;
    if (!question.trim() || !run) return;
    // **버리지 않는다.** 앞 작업이 끝나면 이어서 한다.
    if (this.busy) {
      this.queued = [...this.queued, question.trim()];
      return;
    }
    if (!this.activeId) this.newThread();
    // 서버에 맡긴 일의 끝은 이 대화에 알린다. 그 사이 다른 대화로 옮겨 가도.
    const threadId = this.activeId as string;

    const history = turnsOf(this.messages);
    const answerIndex = this.messages.length + 1;
    this.write([
      ...this.messages,
      { role: 'user', text: question },
      { role: 'assistant', text: '', progress: { kind: 'planning' } },
    ]);
    this.busy = true;
    this.controller = new AbortController();
    // **붙인 것은 이번 턴에만 간다.** 보내는 순간 비운다 — 실패해도 다시 붙이는 편이,
    // 사람 모르게 같은 그림이 계속 따라가는 것보다 낫다.
    const sent = this.attached;
    this.turnFiles = sent;
    this.attached = [];
    this.attachTrouble = '';

    try {
      await run(question, (progress) => {
        const text = progress.kind === 'answering' || progress.kind === 'done'
          ? progress.text
          : (progress.kind === 'ask' ? progress.question : '');
        const messages = this.messages.slice();
        const steps = nextSteps(messages[answerIndex]?.steps ?? [], progress);
        messages[answerIndex] = {
          role: 'assistant',
          text,
          progress,
          steps,
          table: progress.kind === 'done' ? progress.table : undefined,
          routes: progress.kind === 'done' ? progress.routes : undefined,
          experience: progress.kind === 'done' ? progress.experience : undefined,
          formats: progress.kind === 'done' ? progress.formats : undefined,
          result: progress.kind === 'done' ? progress.result : undefined,
          report: progress.kind === 'done' ? progress.report : undefined,
          // 되묻기도 작업을 남긴다. 끝난 턴만 남기면 사용자가 선택지를 누른 다음
          // 턴이 앞서 말한 산출물·기간을 잊는다.
          task: progress.kind === 'done' || progress.kind === 'ask' ? progress.task : undefined,
          queued: progress.kind === 'done' ? progress.queued : undefined,
        };
        this.write(messages);
        if (progress.kind === 'done' && progress.queued?.length) this.watchQueued(threadId, progress.queued);
        // 이번 턴에 배운 이름 짝을 사용자에게 붙여 남긴다. 다음 대화가 같은 현장을
        // 다시 묻지 않게 하는 것이 이 기억의 전부다.
        const learned = (progress.kind === 'done' || progress.kind === 'ask')
          ? progress.task?.aliases
          : undefined;
        if (learned && Object.keys(learned).some((said) => this.aliases[said] !== learned[said])) {
          const fresh = Object.fromEntries(Object.entries(learned)
            .filter(([said, chosen]) => this.aliases[said] !== chosen));
          this.aliases = { ...this.aliases, ...learned };
          const owner = this.owner;
          if (owner) void saveAliases(owner, this.aliases).catch(() => {});
          // 회사 전체가 쓰게 올린다. 실패해도 이 브라우저의 기억은 남는다.
          void window.__elmanAgentAliases?.(fresh).catch(() => ({}));
        }
      }, this.controller.signal, history, this.learned(question), this.auto, this.aliases, sent);
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === 'AbortError';
      if (!aborted) {
        // 진단은 남긴다. 사용자가 "안 된다" 고 할 때 여기부터 본다.
        // eslint-disable-next-line no-console
        console.error('[agent]', error);
        const messages = this.messages.slice();
        // 실패한 턴도 **하던 업무는 남긴다.** 안 남기면 다음 턴이 앞서 말한
        // `워드로 만들어줘` 를 잊은 채 시작해, 답이 나와도 파일 버튼이 없다.
        const carried = [...messages].reverse().find((message) => message.task)?.task;
        messages[answerIndex] = {
          role: 'assistant',
          text: '',
          task: carried,
          // 원문은 콘솔에 남기고, 화면에는 쓰는 사람의 말로 옮겨 보인다. 런타임의
          // 오류 문구는 재계획과 개발자를 향해 쓴 것이라 화면 경로와 컬럼 이름이
          // 들어 있다 — 사무직 직원에게 `/w_tb_e050w` 는 아무 뜻이 없다.
          progress: {
            kind: 'error',
            message: userMessage(error),
          },
        };
        this.write(messages);
      }
    } finally {
      this.busy = false;
      this.controller = undefined;
      await this.persist();
      // **기다리던 것을 이어서 한다.** `busy` 를 내린 **뒤라야** 다음 `ask` 가 다시
      // 줄에 서지 않는다. 취소로 줄이 비었으면 여기서 할 일이 없다.
      const next = this.queued[0];
      if (next !== undefined) {
        this.queued = this.queued.slice(1);
        void this.ask(next);
      }
    }
  }
}
